import React from 'react';
import { theme } from '../styles/theme';

const FishboneDiagram = ({ 
  categories, 
  causes, 
  problem,
  shortDescription,
  causeClassifications,
  containerWidth = 1200,
  containerHeight = 400 
}) => {
  const margin = { top: 40, right: 40, bottom: 40, left: 40 };
  const width = containerWidth - margin.left - margin.right;
  const height = containerHeight - margin.top - margin.bottom;
  const centerY = height / 2;
  
  // Calculate dynamic spacing based on container width
  const spineLength = width * 0.8;
  const categorySpacing = spineLength / (categories.length + 1);
  const problemBoxWidth = Math.min(300, width * 0.25);
  const problemBoxHeight = Math.min(100, height * 0.2);
  const causeBoxWidth = Math.min(300, width * 0.25);
  const causeBoxHeight = Math.min(150, height * 0.25);
  
  // Dynamic font sizing based on container dimensions
  const categoryFontSize = Math.max(14, Math.min(18, width / 60));
  const causeFontSize = Math.max(12, Math.min(16, width / 70));
  const problemFontSize = Math.max(12, Math.min(16, width / 70));

  return (
    <svg 
      width="100%" 
      height="100%" 
      viewBox={`0 0 ${containerWidth} ${containerHeight}`}
      preserveAspectRatio="xMidYMid meet"
      style={{ 
        backgroundColor: 'white',
        border: `1px solid ${theme.colors.border.light}`
      }}
    >
      <g transform={`translate(${margin.left},${margin.top})`}>
        {/* Main spine */}
        <line 
          x1={0} 
          y1={centerY} 
          x2={width} 
          y2={centerY} 
          stroke={theme.colors.primary.main}
          strokeWidth="2" 
        />

        {/* Problem Description Box */}
        <g transform={`translate(${width - problemBoxWidth}, ${centerY - problemBoxHeight/2})`}>
          <rect 
            width={problemBoxWidth} 
            height={problemBoxHeight} 
            fill="white" 
            stroke={theme.colors.primary.main}
          />
          <foreignObject 
            width={problemBoxWidth - 10} 
            height={problemBoxHeight - 10} 
            x={5} 
            y={5}
          >
            <div className="text-center">
              <p style={{ 
                fontWeight: 'bold', 
                color: theme.colors.primary.main,
                fontSize: `${problemFontSize}px` 
              }}>
                Problem Description
              </p>
              <p style={{ 
                marginTop: '4px',
                color: theme.colors.text.secondary,
                fontSize: `${problemFontSize - 2}px` 
              }}>
                {shortDescription || "No problem description available"}
              </p>
            </div>
          </foreignObject>
        </g>

        {/* Cause Classifications Box */}
        <g transform={`translate(${width - causeBoxWidth}, ${centerY + problemBoxHeight/2 + 10})`}>
          <foreignObject 
            width={causeBoxWidth} 
            height={causeBoxHeight}
          >
            <div style={{ 
              color: theme.colors.text.secondary,
              fontSize: `${problemFontSize - 2}px`
            }}>
              <p className="mb-1">
                <strong style={{ color: theme.colors.primary.main }}>Root Causes:</strong>
                {' '}{causeClassifications.root.join(', ')}
              </p>
              <p className="mb-1">
                <strong style={{ color: theme.colors.primary.main }}>Contributing:</strong>
                {' '}{causeClassifications.contributing.join(', ')}
              </p>
              <p className="mb-1">
                <strong style={{ color: theme.colors.primary.main }}>Possible:</strong>
                {' '}{causeClassifications.possible.join(', ')}
              </p>
            </div>
          </foreignObject>
        </g>

        {/* Categories and Causes */}
        {categories.map((category, index) => {
          const x = (index + 1) * categorySpacing;
          const isUpper = index % 2 === 0;
          const yDirection = isUpper ? -1 : 1;
          const branchLength = Math.min(200, height * 0.3);
          
          return (
            <g key={category}>
              {/* Category branch */}
              <line
                x1={x}
                y1={centerY}
                x2={x}
                y2={centerY + yDirection * branchLength}
                stroke={theme.colors.primary.main}
                strokeWidth="2"
              />
              
              {/* Category label */}
              <text
                x={x}
                y={centerY + yDirection * (branchLength + 20)}
                textAnchor="middle"
                dominantBaseline={isUpper ? "auto" : "hanging"}
                style={{ 
                  fill: theme.colors.primary.main,
                  fontWeight: 'bold',
                  fontSize: `${categoryFontSize}px`
                }}
              >
                {category}
              </text>

              {/* Causes */}
              {causes[category]?.map((cause, causeIndex) => {
                const yOffset = 25 + causeIndex * (causeFontSize + 6);
                return (
                  <text
                    key={causeIndex}
                    x={x + (isUpper ? 8 : -8)}
                    y={centerY + yDirection * yOffset}
                    textAnchor={isUpper ? "start" : "end"}
                    dominantBaseline="middle"
                    style={{ 
                      fill: theme.colors.text.secondary,
                      fontSize: `${causeFontSize}px`
                    }}
                  >
                    {cause}
                  </text>
                );
              })}
            </g>
          );
        })}
      </g>
    </svg>
  );
};

export default FishboneDiagram;
